import { useEffect, useRef, useState, type MouseEvent } from 'react';
import {
  Box,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Popover,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import LoopOutlinedIcon from '@mui/icons-material/LoopOutlined';
import NumberSpinner from '@/components/common/number-spinner';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key';
import type { SequencedPlaybackFormValues } from '@/components/media/sequenced-content-editor';
import {
  DEFAULT_DWELL_MS,
  DWELL_PERSIST_DEBOUNCE_MS,
  MAX_DWELL_SECONDS,
  MIN_DWELL_SECONDS,
  dwellMsToSeconds,
  dwellSecondsToMs,
} from '@/helpers/sequenced-playback.helper';

type MediaItemAutoLoopDwellButtonProps = {
  dwellMs: number;
  disabled?: boolean;
  loading?: boolean;
  testId?: string;
  /** Match flow Control header rail icons (1.75rem) vs default playlist header icons (1.5rem). */
  size?: 'default' | 'flowControl';
  onPlaybackChange: (next: SequencedPlaybackFormValues) => void;
};

const AUTO_LOOP_ICON_SIZES = {
  default: { box: 24, loop: 24, seconds: '0.625rem' },
  flowControl: { box: 28, loop: 28, seconds: '0.7rem' },
} as const;

export function MediaItemAutoLoopDwellButton({
  dwellMs,
  disabled = false,
  loading = false,
  testId,
  size = 'default',
  onPlaybackChange,
}: MediaItemAutoLoopDwellButtonProps) {
  const t = useTranslations();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [localValue, setLocalValue] = useState<SequencedPlaybackFormValues>({
    loopEnabled: true,
    dwellMs: dwellMs || DEFAULT_DWELL_MS,
  });
  const localValueRef = useRef(localValue);
  const persistDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onPlaybackChangeRef = useRef(onPlaybackChange);

  useEffect(() => {
    localValueRef.current = localValue;
  }, [localValue]);

  useEffect(() => {
    onPlaybackChangeRef.current = onPlaybackChange;
  }, [onPlaybackChange]);

  useEffect(() => {
    if (persistDebounceRef.current) {
      return;
    }
    setLocalValue((current) => ({
      ...current,
      dwellMs: dwellMs || DEFAULT_DWELL_MS,
    }));
  }, [dwellMs]);

  useEffect(
    () => () => {
      if (persistDebounceRef.current) {
        clearTimeout(persistDebounceRef.current);
        onPlaybackChangeRef.current(localValueRef.current);
      }
    },
    [],
  );

  const clearPersistDebounce = () => {
    if (persistDebounceRef.current) {
      clearTimeout(persistDebounceRef.current);
      persistDebounceRef.current = null;
    }
  };

  const persistNow = (next: SequencedPlaybackFormValues) => {
    clearPersistDebounce();
    onPlaybackChangeRef.current(next);
  };

  const scheduleDwellPersist = (next: SequencedPlaybackFormValues) => {
    clearPersistDebounce();
    persistDebounceRef.current = setTimeout(() => {
      persistDebounceRef.current = null;
      onPlaybackChangeRef.current(next);
    }, DWELL_PERSIST_DEBOUNCE_MS);
  };

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (disabled || loading) {
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    if (persistDebounceRef.current) {
      clearPersistDebounce();
      onPlaybackChangeRef.current(localValueRef.current);
    }
    setAnchorEl(null);
  };

  const dwellSeconds = dwellMsToSeconds(localValue.dwellMs);
  const tooltip = t('media.sequencedContent.dwellInSeconds' as TranslationKey);
  const iconSize = AUTO_LOOP_ICON_SIZES[size];

  return (
    <>
      <Tooltip title={tooltip} placement="top">
        <IconButton
          size="small"
          aria-label={tooltip}
          disabled={disabled || loading}
          onClick={handleOpen}
          data-testid={testId}
          sx={{
            padding: '2px',
            color: 'info.main',
          }}
        >
          {loading ? (
            <CircularProgress color="info" size={20} sx={{ width: '1.25rem', height: '1.25rem' }} />
          ) : (
            <Box
              sx={{
                position: 'relative',
                width: iconSize.box,
                height: iconSize.box,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LoopOutlinedIcon sx={{ fontSize: iconSize.loop, width: iconSize.loop, height: iconSize.loop }} />
              <Typography
                component="span"
                sx={{
                  position: 'absolute',
                  fontSize: iconSize.seconds,
                  fontWeight: 700,
                  lineHeight: 1,
                  color: 'info.main',
                }}
              >
                {dwellSeconds}
              </Typography>
            </Box>
          )}
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClick={(event) => event.stopPropagation()}
      >
        <Stack spacing={1.5} sx={{ p: 1.5, minWidth: 200 }} onClick={(event) => event.stopPropagation()}>
          <FormControlLabel
            sx={{ m: 0 }}
            control={
              <Switch
                size="small"
                color="secondary"
                checked={localValue.loopEnabled}
                disabled={disabled}
                onChange={(event) => {
                  const next = { ...localValueRef.current, loopEnabled: event.target.checked };
                  setLocalValue(next);
                  persistNow(next);
                  if (!event.target.checked) {
                    setAnchorEl(null);
                  }
                }}
              />
            }
            label={t('media.sequencedContent.autoLoop' as TranslationKey)}
          />
          <NumberSpinner
            label={t('media.sequencedContent.dwellInSeconds' as TranslationKey)}
            size="small"
            min={MIN_DWELL_SECONDS}
            max={MAX_DWELL_SECONDS}
            step={1}
            value={dwellSeconds}
            disabled={disabled || !localValue.loopEnabled}
            onValueChange={(nextSeconds) => {
              if (nextSeconds == null) {
                return;
              }
              const next = {
                ...localValueRef.current,
                dwellMs: dwellSecondsToMs(nextSeconds),
              };
              setLocalValue(next);
              scheduleDwellPersist(next);
            }}
          />
        </Stack>
      </Popover>
    </>
  );
}
